import { useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { MiDatePicker, MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { formatDate, isSameDay } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCalendarStartOutline } from "@mdi/js";
import { useTheme } from "@mui/material";

interface NhcRescheduleOrderModalProps {
  open: boolean;
  onClose: () => void;
  onReschedule: (data: {
    contactName: string;
    rescheduleReasonId: string;
    rescheduleDate: string;
    confirmAfterReschedule?: boolean;
  }) => Promise<void>;
  orderReasons: {
    rescheduleReasons: { value: string; label: string }[];
  };
  isLoading: boolean;
  currentScheduledDate?: string;
}

const createRescheduleSchema = (currentScheduledDate?: string) =>
  z.object({
    contactName: z.string().min(1, "Contact Name is required."),
    rescheduleReasonId: z
      .string()
      .uuid()
      .min(1, "Reschedule Reason is required."),
    confirmAfterReschedule: z.boolean().optional(),
    rescheduleDate: z
      .string()
      .refine((value) => value !== "Invalid date", {
        message: "The value cannot be 'Invalid date'",
      })
      .refine(
        (value) => {
          if (!currentScheduledDate) return true;
          const reschedule = formatDate(
            value,
            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
          );
          const current = formatDate(
            currentScheduledDate,
            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
          );

          return !isSameDay(reschedule, current);
        },
        {
          message:
            "Reschedule date cannot be the same as the current scheduled date.",
        }
      ),
  });

const initialRescheduleValues = {
  contactName: "",
  rescheduleReasonId: "",
  rescheduleDate: formatDate(
    new Date(),
    DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
  ),
  confirmAfterReschedule: false,
};

export const NhcRescheduleOrderModal = ({
  open,
  onClose,
  onReschedule,
  orderReasons,
  isLoading,
  currentScheduledDate,
}: NhcRescheduleOrderModalProps) => {
  const theme = useTheme();
  const rescheduleSchema = useMemo(
    () => createRescheduleSchema(currentScheduledDate),
    [currentScheduledDate]
  );
  const {
    setValue,
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: initialRescheduleValues,
    resolver: zodResolver(rescheduleSchema),
  });

  const rescheduleDate = useWatch({
    control,
    name: "rescheduleDate",
    defaultValue: initialRescheduleValues.rescheduleDate,
  });

  const handleDateChange = useCallback(
    (date: Date) => {
      setValue(
        "rescheduleDate",
        formatDate(date, DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [setValue]
  );

  const rescheduleModalFields = useMemo(
    () => [
      {
        label: "Contact Name",
        name: "contactName",
        width: "100%",
        required: true,
        formControl: control,
      },
      {
        label: "Reschedule Reason",
        name: "rescheduleReasonId",
        options: orderReasons?.rescheduleReasons,
        fieldType: INPUT_TYPES.SELECT,
        required: true,
        formControl: control,
      },
      {
        label: "Reschedule Date",
        name: "rescheduleDate",
        required: true,
        fieldType: INPUT_TYPES.CUSTOM,
        formControl: control,
        width: "100%",
        customComponent: (
          <MiDatePicker
            value={rescheduleDate}
            onChange={handleDateChange}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            error={errors.rescheduleDate?.message}
          />
        ),
      },
      {
        label: "Confirm Order After Reschedule",
        name: "confirmAfterReschedule",
        fieldType: INPUT_TYPES.CHECKBOX,
        formControl: control,
        handleChange: ({ target: { checked } }: any) => {
          setValue("confirmAfterReschedule", checked, {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
        width: "100%",
        style: { padding: theme.spacing(0.5, 0) },
      },
    ],
    [
      orderReasons,
      control,
      handleDateChange,
      rescheduleDate,
      errors.rescheduleDate,
    ]
  );

  const handleFormSubmit = async (data: typeof initialRescheduleValues) => {
    await onReschedule(data);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={330}
      titleIcon={mdiCalendarStartOutline}
      leftIconColor={theme.palette.primary.main}
      title="Change Requested Date"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Change Requested Date",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: isLoading || isSubmitting,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <MiDetailFields detailFields={rescheduleModalFields} />
    </MiModal>
  );
};
